import React from 'react';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDiagramProject,
    faClipboardCheck,
    faBoxOpen,
    faChartLine,
    faUserGear,
    faGears,
    faTruckFast,
    faSquareCheck,
    faUsersViewfinder,
    faCalendarWeek,
    faCalendarDays,
    faGaugeHigh
} from '@fortawesome/free-solid-svg-icons';
import decklepro_logo from '../images/decklepro.png'; // Update with your logo path

const ProductDetails = () => (
    <main className="bg-light">
        <Helmet>
            <title>DecklePro - Smart Deckle Matching Software for Kraft Paper Mills</title>
            <meta
                name="description"
                content="Looking for the best deckle match software for kraft paper mills? DecklePro helps reduce trim waste, automate knife setup, improve yield, and optimize reel slitting plans. Learn how to eliminate manual deckle planning and boost mill efficiency today."
            />
            <meta
                name="keywords"
                content="Deckle, Deckle Matching, Deckle Match, Deckle Matching Software, Deckle Match Software, Deckle Software, Deckle app, Deckle web, Deckle technology, Kraft Paper software, Paper mill software, Best software for paper mill planning, Best deckle match software, Best deckle, Best deckle software, Deckle optimization tool, Kraft paper deckle software, Paper slitting planning software, Deckle matching automation, Reel planning system, Trim waste reduction software, Deckle software India, Deckle software for global paper mills, Kraft paper mill software India, Slitting optimization India, Paper mill planning software worldwide, How to reduce trim waste in paper mills, Deckle optimization calculator, Software to plan knife setup, Improve yield in kraft paper slitting, Tool to match reels to knives"
            />
            <link rel="canonical" href="https://www.anmatics.com/product-details" />
            <meta property="og:title" content="DecklePro - Smart Deckle Matching Software for Kraft Paper Mills" />
            <meta property="og:description" content="Powerful deckle optimization software designed for Kraft paper mills. Save material, automate knife setups, and increase productivity." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://www.anmatics.com/product-details" />
        </Helmet>
        {/* Hero Section */}
        <header
            className="text-white text-center py-3"
            style={{
                background: 'linear-gradient(90deg, var(--anmatics-primary), var(--anmatics-primary-hover))',
                boxShadow: '0 4px 24px rgba(28,101,149,0.15)'
            }}
        >
            <div className="container">
                <img
                    src={decklepro_logo}
                    alt="DecklePro Logo"
                    style={{
                        height: '80px',
                        marginBottom: '20px',
                        background: '#fff',
                        borderRadius: '16px',
                        boxShadow: '0 2px 8px rgba(28,101,149,0.08)'
                    }}
                />
                <h4 className="display-6 fw-bold" style={{ letterSpacing: '1.5px' }}>
                    <span style={{ color: '#ffffff', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>Deckle</span>
                    <span style={{ color: '#7aa056', textShadow: '2px 2px 4px rgba(0,0,0,0.2)' }}>Pro</span>
                    <small style={{ color: '#ffffff', fontSize: '1.5rem', marginLeft: '8px' }}>– Smart Deckle Matching Software</small>
                </h4>
            </div>
        </header>

        <div className="container mt-5 px-md-5">
            <div
                className="rounded-4 shadow-lg p-4 p-md-5 position-relative"
                style={{
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                }}
            >
                <div className="mb-4">
                    <h5 className="fw-bold" style={{ color: '#7aa056', fontSize: '1.5rem' }}>
                        Why DecklePro Stands Out for Kraft Paper Mills
                    </h5>
                    <p className="text-muted mb-0">Here’s how DecklePro transforms your operations with efficiency and precision:</p>
                </div>
                <ul className="list-unstyled row">
                    {[
                        "Purpose-built for kraft paper slitting and deckle optimization",
                        "Automates reel matching and knife setup",
                        "Minimizes trim waste, improving raw material yield significantly",
                        "Integrates order and stock tracking into one intelligent dashboard",
                        "Enables real-time insights with exportable performance reports",
                        "Empowers your team with faster planning and smarter execution"
                    ].map((text, idx) => (
                        <li
                            key={idx}
                            className="col-md-6 d-flex align-items-start mb-3"
                        >
                            <div
                                className="me-3 d-flex justify-content-center align-items-center"
                                style={{
                                    width: '32px',
                                    height: '32px',
                                    backgroundColor: '#7aa0561a',
                                    borderRadius: '50%',
                                    color: '#7aa056',
                                    fontSize: '1.1rem'
                                }}
                            >
                                ✓
                            </div>
                            <div style={{ color: '#333' }}>{text}</div>
                        </li>
                    ))}
                </ul>
            </div>
        </div>

        {/* Features */}
        <section className="py-5">
            <div className="container">
                <h3 className="text-center mb-5 fw-bold contact-title" style={{ color: 'var(--anmatics-primary)' }}>
                    Core Features of DecklePro – Deckle Optimization Software
                </h3>
                {/*
                  Updated feature cards with rounded icons and dynamic colors
                */}
                <div className="row g-4">
                    {(() => {
                        const iconColors = [
                            { bg: '#e8f0fe', color: '#2563eb', icon: faDiagramProject },
                            { bg: '#dcfce7', color: '#16a34a', icon: faClipboardCheck },
                            { bg: '#f3e8ff', color: '#7e22ce', icon: faBoxOpen },
                            { bg: '#fff7ed', color: '#ea580c', icon: faChartLine },
                            { bg: '#e0f2fe', color: '#0284c7', icon: faUserGear },
                            { bg: '#fef9c3', color: '#ca8a04', icon: faGears },
                            { bg: '#fce7f3', color: '#be185d', icon: faTruckFast },
                            { bg: '#ede9fe', color: '#6d28d9', icon: faSquareCheck },
                            { bg: '#cffafe', color: '#0e7490', icon: faUsersViewfinder },
                            { bg: '#fee2e2', color: '#dc2626', icon: faCalendarWeek },
                            { bg: '#fef3c7', color: '#d97706', icon: faCalendarDays },
                            { bg: '#d1fae5', color: '#059669', icon: faGaugeHigh }
                        ];
                        const features = [
                            {
                                title: "Deckle Match Engine",
                                desc: "Minimize material waste with intelligent reel matching. Automatically generate optimal knife combinations and reduce trim loss."
                            },
                            {
                                title: "Order Management",
                                desc: "Track and manage customer orders seamlessly, from entry to dispatch. Prioritize, assign, and fulfill orders on time."
                            },
                            {
                                title: "Stock Management",
                                desc: "Maintain complete visibility of your reel inventory. Manage stock by size, weight, GSM, and reel status efficiently."
                            },
                            {
                                title: "Advanced Reporting",
                                desc: "Generate insightful reports for deckle efficiency, order performance, stock levels, and waste analysis all exportable."
                            },
                            {
                                title: "User Management",
                                desc: "Secure your platform with role-based access control. Assign roles for admin, planner, manager, and production staff."
                            },
                            {
                                title: "Master Setup",
                                desc: "Fully configurable system. Set up product types, tolerances, knife rules, and machinery specs to match your workflow."
                            },
                            // New features begin here
                            {
                                title: "Transport Planning",
                                desc: "Plan and optimize logistics for dispatch. Assign vehicles, routes, and schedules seamlessly."
                            },
                            {
                                title: "Dispatch Details",
                                desc: "Track dispatch status, vehicle allocation, and delivery timelines in real time."
                            },
                            {
                                title: "Customer Management",
                                desc: "Maintain customer records, order history, preferences, and communication logs securely."
                            },
                            {
                                title: "Production Plan",
                                desc: "Define production priorities and align them with incoming orders and inventory status."
                            },
                            {
                                title: "Production Schedule",
                                desc: "View machine-wise schedules, shift-wise plans, and execution timelines to boost efficiency."
                            },
                            {
                                title: "Dashboard",
                                desc: "Get a real-time visual overview of operations, KPIs, alerts, and bottlenecks on one screen."
                            }
                        ];
                        return features.map((feature, idx) => {
                            const { bg, color, icon } = iconColors[idx % iconColors.length];
                            return (
                                <div className="col-md-4" key={feature.title}>
                                    <div
                                        className="feature-card bg-white p-4 shadow-sm rounded-3 h-100 animate-fadeIn d-flex flex-column align-items-center text-center"
                                        style={{
                                            borderTop: `4px solid var(--anmatics-primary)`,
                                            transition: 'transform 0.3s, box-shadow 0.3s'
                                        }}
                                    >
                                        <div className="d-flex justify-content-center align-items-center mb-3 rounded-4"
                                            style={{ width: 64, height: 64, backgroundColor: bg }}>
                                            <FontAwesomeIcon icon={icon} style={{ color }} size="lg" />
                                        </div>
                                        <h5 className="fw-semibold" style={{ color: 'var(--anmatics-primary-hover)' }}>{feature.title}</h5>
                                        <p className="mb-0">{feature.desc}</p>
                                    </div>
                                </div>
                            );
                        });
                    })()}
                </div>
            </div>
        </section>

        {/* Statistics Section */}
        <section
            className="py-5"
            style={{
                background: 'linear-gradient(135deg, var(--anmatics-primary) 0%, var(--anmatics-primary-hover) 100%)'
            }}
        >
            <div className="container">
                <div className="row text-center text-white">
                    <div className="col-lg-3 col-md-6 mb-4" data-aos="fade-up" data-aos-delay="100">
                        <div className="p-4">
                            <div className="display-4 fw-bold mb-2">
                                <i className="bi bi-graph-up text-warning me-2"></i>
                                <span className="counter">90</span>%
                            </div>
                            <h5>Waste Reduction</h5>
                            <p className="mb-0 opacity-75">Average trim waste reduction achieved</p>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-4" data-aos="fade-up" data-aos-delay="200">
                        <div className="p-4">
                            <div className="display-4 fw-bold mb-2">
                                <i className="bi bi-speedometer2 text-success me-2"></i>
                                <span className="counter">50</span>%
                            </div>
                            <h5>Faster Processing</h5>
                            <p className="mb-0 opacity-75">Quicker order processing time</p>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-4" data-aos="fade-up" data-aos-delay="300">
                        <div className="p-4">
                            <div className="display-4 fw-bold mb-2">
                                <i className="bi bi-people text-info me-2"></i>
                                <span className="counter">5</span>+
                            </div>
                            <h5>Happy Clients</h5>
                            <p className="mb-0 opacity-75">Satisfied customers worldwide</p>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-4" data-aos="fade-up" data-aos-delay="400">
                        <div className="p-4">
                            <div className="display-4 fw-bold mb-2">
                                <i className="bi bi-clock text-warning me-2"></i>
                                24<span className="fs-2">/7</span>
                            </div>
                            <h5>Support</h5>
                            <p className="mb-0 opacity-75">Round the clock assistance</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        {/* Process Section */}
        <section className="py-5 process-section">
            <div className="container">
                <div className="text-center mb-5">
                    <h2 className="fw-bold section-title" style={{ color: 'var(--anmatics-primary)' }}>How It Works</h2>
                    <p className="lead text-muted">Simple steps to transform your paper mill operations</p>
                </div>
                <div className="row">
                    {[
                        {
                            number: 1,
                            title: "Setup & Configuration",
                            desc: "Configure your system with paper types, machinery specs, and business rules"
                        },
                        {
                            number: 2,
                            title: "Input Orders & Stock",
                            desc: "Enter customer orders and maintain real-time inventory of paper rolls"
                        },
                        {
                            number: 3,
                            title: "Smart Matching",
                            desc: "Let our deckle engine find optimal deckle combinations to minimize waste"
                        },
                        {
                            number: 4,
                            title: "Execute & Report",
                            desc: "Process orders efficiently and generate detailed performance reports"
                        }
                    ].map((step, idx) => (
                        <div className="col-lg-3 col-md-6 mb-4" data-aos="fade-up" data-aos-delay={100 * (idx + 1)} key={step.title}>
                            <div className="bg-white p-4 shadow-sm rounded-3 h-100 text-center animate-fadeIn process-step" style={{ borderTop: `4px solid var(--anmatics-primary)` }}>
                                <div className="process-number fw-bold mb-3" style={{
                                    width: 48,
                                    height: 48,
                                    lineHeight: '48px',
                                    borderRadius: '50%',
                                    background: 'var(--anmatics-primary-hover)',
                                    color: '#fff',
                                    fontSize: 24,
                                    margin: '0 auto 16px auto'
                                }}>{step.number}</div>
                                <h5 className="fw-semibold" style={{ color: 'var(--anmatics-primary-hover)' }}>{step.title}</h5>
                                <p className="mb-0">{step.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>


        {/* Benefits */}
        <section className="bg-white py-5 border-top border-bottom">
            <div className="container text-center">
                <h2 className="fw-bold mb-4 contact-title" style={{ color: 'var(--anmatics-primary)' }}>Why Choose DecklePro?</h2>
                <div className="row justify-content-center">
                    <div className="col-md-8">
                        <ul className="list-group list-group-flush text-start fs-6 shadow-sm rounded-3">
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Reduce trim waste and improve yield per parent roll</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Automate reel matching and knife setup calculations</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Track orders and inventory with precision</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Generate real-time reports for smarter planning</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Enable secure, role-based system access for teams</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Highly customizable to suit different paper mills</li>
                            {/* SEO-targeted benefits */}
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Plan and optimize deckle matching using advanced algorithms</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Simulate knife setups before production begins</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Reduce edge trim loss and material scrap</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Increase machine utilization with smarter scheduling</li>
                            <li className="list-group-item" style={{ borderLeft: '4px solid var(--anmatics-primary)' }}>✓ Designed for kraft paper mills and roll slitting operations</li>
                        </ul>
                        <p className="mt-4 fs-6 text-center">
                            Want to know how it compares to manual planning? <a href="/blogs/manual-vs-software-deckle-matching" style={{ color: 'var(--anmatics-primary-hover)' }}>Read our detailed comparison</a><br />
                            Also check out: <a href="/blogs/top-5-mistakes-in-reel-slitting-operations" style={{ color: 'var(--anmatics-primary-hover)' }}>Top 5 Mistakes in Reel Slitting Operations</a> and <a href="/blogs/why-deckle-matching-optimization-saves-costs" style={{ color: 'var(--anmatics-primary-hover)' }}>Why Deckle Matching Optimization Can Save Lakhs</a>.
                        </p>
                    </div>
                </div>
            </div>
        </section>

        {/* FAQ Section */}
        <section className="py-5 bg-light border-top">
            <div className="container">
                <h2 className="fw-bold text-center mb-4" style={{ color: 'var(--anmatics-primary)' }}>Frequently Asked Questions</h2>
                <div className="accordion" id="faqAccordion">
                    {[
                        {
                            question: "How can I reduce trim waste in my paper mill?",
                            answer: "DecklePro uses deckle matching engine to generate optimal knife setups, significantly reducing trim waste and improving raw material yield."
                        },
                        {
                            question: "What is the best software for deckle matching?",
                            answer: "DecklePro is purpose-built for kraft paper mills, offering intelligent deckle match suggestions, knife setup planning, and slitting optimization-all in one platform."
                        },
                        {
                            question: "Can I simulate knife combinations before production?",
                            answer: "Yes, DecklePro includes simulation tools that allow planners to preview multiple deckle layouts and knife setups before sending them to the shop floor."
                        },
                        {
                            question: "Does DecklePro support paper mills in India and abroad?",
                            answer: "Absolutely. DecklePro is used by kraft paper mills across India and is ready to scale globally with multi-user access and customizable settings."
                        }
                    ].map((faq, idx) => (
                        <div className="accordion-item mb-3" key={idx}>
                            <h2 className="accordion-header" id={`faqHeading${idx}`}>
                                <button
                                    className="accordion-button collapsed"
                                    type="button"
                                    data-bs-toggle="collapse"
                                    data-bs-target={`#faqCollapse${idx}`}
                                    aria-expanded="false"
                                    aria-controls={`faqCollapse${idx}`}
                                    style={{ fontWeight: 600 }}
                                >
                                    {faq.question}
                                </button>
                            </h2>
                            <div
                                id={`faqCollapse${idx}`}
                                className="accordion-collapse collapse"
                                aria-labelledby={`faqHeading${idx}`}
                                data-bs-parent="#faqAccordion"
                            >
                                <div className="accordion-body">
                                    {faq.answer}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* Call to Action */}
        <section
            className="py-5 text-white text-center"
            style={{
                background: 'linear-gradient(90deg, var(--anmatics-primary-hover), var(--anmatics-primary))'
            }}
        >
            <div className="container">
                <h2 className="fw-bold mb-3 animate-slideDown">Transform Your Paper Mill Planning</h2>
                <p className="mb-4 animate-fadeIn">Start using DecklePro today and streamline your production, reduce waste, and increase profitability.</p>
                <a
                    href="/contact"
                    className="btn btn-light btn-lg px-4"
                    style={{
                        color: 'var(--anmatics-primary)',
                        fontWeight: 600,
                        border: `2px solid var(--anmatics-primary-hover)`,
                        boxShadow: '0 2px 8px rgba(28,101,149,0.08)'
                    }}
                >
                    Request a Demo
                </a>
            </div>
        </section>
        <Helmet>
            <script type="application/ld+json">
                {`
              {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "How can I reduce trim waste in my paper mill?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "DecklePro uses deckle matching engine to generate optimal knife setups, significantly reducing trim waste and improving raw material yield."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What is the best software for deckle matching?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "DecklePro is purpose-built for kraft paper mills, offering intelligent deckle match suggestions, knife setup planning, and slitting optimization-all in one platform."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Can I simulate knife combinations before production?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes, DecklePro includes simulation tools that allow planners to preview multiple deckle layouts and knife setups before sending them to the shop floor."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Does DecklePro support paper mills in India and abroad?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Absolutely. DecklePro is used by kraft paper mills across India and is ready to scale globally with multi-user access and customizable settings."
                    }
                  }
                ]
              }
            `}
            </script>
        </Helmet>
    </main>
);

export default ProductDetails;